import React, { useEffect, useState } from "react";
import { Video } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";

export default function Telehealth() {
  const [visits, setVisits] = useState([]);
  const [rules, setRules] = useState([]);
  const [payer, setPayer] = useState([]);

  useEffect(() => {
    api.get("/telehealth/visits").then(r=>setVisits(r.data));
    api.get("/telehealth/rules").then(r=>setRules(r.data));
    api.get("/telehealth/payer-rules").then(r=>setPayer(r.data));
  }, []);

  const avg = visits.length ? Math.round(visits.reduce((s,v)=>s+v.quality_score,0)/visits.length) : 0;
  const complete = visits.filter(v=>v.complete).length;

  const visitCols = [
    { key: "patient", label: "Patient" },
    { key: "date", label: "Date" },
    { key: "duration_min", label: "Duration", render: (r)=>`${r.duration_min} min` },
    { key: "quality_score", label: "Quality", render: (r)=><span className={`status-pill ${r.quality_score>=85?"status-approved":r.quality_score>=70?"status-pending":"status-denied"}`}>{r.quality_score}</span> },
    { key: "complete", label: "Complete?", render: (r)=>r.complete?"Yes":"No" },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 23" title="Telehealth Intelligence"
        subtitle="Visit quality scoring, state prescribing rules, payer-specific virtual reimbursement and tech-barrier detection." testid="telehealth-page" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiCard icon={Video} label="Avg Quality Score" value={`${avg}/100`} testid="kpi-quality" />
        <KpiCard label="Complete encounters" value={`${complete}/${visits.length}`} testid="kpi-complete" />
        <KpiCard label="States covered" value={rules.length} accent testid="kpi-states" />
      </div>

      <SectionCard title="Recent Telehealth Visits" testid="visits-section">
        <DataTable data={visits} columns={visitCols} searchKeys={["patient"]} testid="visits-table" />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="State Prescribing Rules" testid="state-rules">
          <div className="space-y-2 text-sm">
            {rules.map((r,i)=>(
              <div key={i} className="p-3 border border-brand rounded-md">
                <div className="font-semibold text-brand-primary">{r.state}</div>
                <div className="text-xs text-brand-muted mt-1">Audio-only: {r.audio_only_allowed?"Allowed":"Not allowed"}</div>
                <div className="text-xs text-brand-muted">Controlled subs: {r.controlled_substance_prescribing}</div>
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard title="Payer Billing Rules" testid="payer-rules">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr><th className="text-left px-2 py-2 text-xs uppercase text-brand-muted">Payer</th><th className="text-left px-2 py-2 text-xs uppercase text-brand-muted">Video Req</th><th className="text-left px-2 py-2 text-xs uppercase text-brand-muted">Audio Paid</th><th className="text-left px-2 py-2 text-xs uppercase text-brand-muted">Modifier</th></tr></thead>
              <tbody>
                {payer.map((p,i)=>(<tr key={i} className="border-t border-brand"><td className="px-2 py-2 font-medium">{p.payer}</td><td className="px-2 py-2">{p.video_required?"Yes":"No"}</td><td className="px-2 py-2">{p.audio_only_paid?"Yes":"No"}</td><td className="px-2 py-2 font-mono text-brand-primary">{p.modifier}</td></tr>))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>
    </>
  );
}
